import { db } from "../firebase";
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  getDocs,
  updateDoc,
  doc,
  serverTimestamp,
  deleteDoc,
  limit,
  onSnapshot,
} from "firebase/firestore";

const notificationsCollection = collection(db, "notifications");

// Create notification for admin users when non-admin activity occurs
export const createNotification = async (notification) => {
  try {
    let recipientIds = [];

    if (notification.targetUserId) {
      recipientIds = [notification.targetUserId];
    } else {
      // Get all admin users
      const usersSnapshot = await getDocs(
        query(collection(db, "users"), where("role", "==", "admin"))
      );
      recipientIds = usersSnapshot.docs.map((doc) => doc.id);
    }

    if (recipientIds.length === 0) {
      console.warn("No users found to send notifications to");
      return false;
    }

    // Create notification for each recipient
    const notificationPromises = recipientIds.map((recipientId) =>
      addDoc(notificationsCollection, {
        userId: recipientId, // User who will receive the notification
        actorId: notification.actorId, // User who performed the action
        actorName: notification.actorName,
        actorRole: notification.actorRole,
        title: notification.title,
        message: notification.message,
        type: notification.type,
        actionType: notification.actionType || "general",
        isRead: false,
        createdAt: serverTimestamp(),
        metadata: notification.metadata || {},
      })
    );

    await Promise.all(notificationPromises);

    // The Firestore-triggered Firebase Function sends the browser push.
    return true;
  } catch (error) {
    console.error("Error creating notification:", error);
    return false;
  }
};

// Get unread notifications for a user
export const getUnreadNotifications = async (userId) => {
  try {
    const q = query(
      notificationsCollection,
      where("userId", "==", userId),
      where("isRead", "==", false),
      orderBy("createdAt", "desc")
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
    }));
  } catch (error) {
    console.error("Error fetching unread notifications:", error);
    return [];
  }
};

// Get all notifications for a user (with limit)
export const getUserNotifications = async (userId, limitCount = 20) => {
  try {
    const q = query(
      notificationsCollection,
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
    }));
  } catch (error) {
    console.error("Error fetching notifications:", error);
    return [];
  }
};

// Mark notification as read
export const markAsRead = async (notificationId) => {
  try {
    await updateDoc(doc(db, "notifications", notificationId), {
      isRead: true,
      readAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    console.error("Error marking notification as read:", error);
    return false;
  }
};

// Mark all notifications as read for a user
export const markAllAsRead = async (userId) => {
  try {
    const q = query(
      notificationsCollection,
      where("userId", "==", userId),
      where("isRead", "==", false)
    );

    const snapshot = await getDocs(q);
    const updatePromises = snapshot.docs.map((docSnap) =>
      updateDoc(doc(db, "notifications", docSnap.id), {
        isRead: true,
        readAt: serverTimestamp(),
      })
    );

    await Promise.all(updatePromises);
    return true;
  } catch (error) {
    console.error("Error marking all notifications as read:", error);
    return false;
  }
};

// Delete notification
export const deleteNotification = async (notificationId) => {
  try {
    await deleteDoc(doc(db, "notifications", notificationId));
    return true;
  } catch (error) {
    console.error("Error deleting notification:", error);
    return false;
  }
};

// Delete all read notifications older than 7 days
export const cleanupOldNotifications = async (userId) => {
  try {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const q = query(
      notificationsCollection,
      where("userId", "==", userId),
      where("isRead", "==", true),
      where("createdAt", "<", sevenDaysAgo)
    );

    const snapshot = await getDocs(q);
    const deletePromises = snapshot.docs.map((docSnap) =>
      deleteDoc(doc(db, "notifications", docSnap.id))
    );

    await Promise.all(deletePromises);
    return snapshot.docs.length;
  } catch (error) {
    console.error("Error cleaning up old notifications:", error);
    return 0;
  }
};

// Get unread count
export const getUnreadCount = async (userId) => {
  try {
    const notifications = await getUnreadNotifications(userId);
    return notifications.length;
  } catch (error) {
    console.error("Error getting unread count:", error);
    return 0;
  }
};

// Real-time subscription to user notifications
export const subscribeToUserNotifications = (
  userId,
  onUpdate,
  onError
) => {
  try {
    const q = query(
      notificationsCollection,
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(20)
    );

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const notifs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
        }));

        // Calculate unread count
        const unread = notifs.filter((n) => !n.isRead).length;

        onUpdate(notifs, unread);
      },
      (error) => {
        console.error("Error in real-time notification listener:", error);
        if (onError) {
          onError(error);
        }
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error("Error setting up notification subscription:", error);
    if (onError) {
      onError(error);
    }
    return () => {}; // Return dummy unsubscribe function
  }
};
